import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store";
import GlobalWidgets from "../widgets/GlobalWidgets";
import useGetUrlLocale from "../../hooks/useGetUrlLocale";
import { useClickHandlers } from "../../service/desktopNavigationService";
import { ScreenShotService } from "../../service/ScreenShot/ScreenShotService";
import { PageNode } from "../../types/PageNode";
import { useCheckLocation } from "../../hooks/useCheckLocation";
import { useScreenShot } from "../../hooks/useScreenShot";
import { useDeletePageSocialLinksMutation } from "../../service/Page/PageService.hooks";
import { ALL_ALLOWED_FILES, LOCALES } from "../../data/constants";
import { SocialNetworkService } from "../../service/Page/SocialNetworkService";
import { changeNode, setNodes } from "../../reducers/nodes";
import RenderOnce from "../../utils/renderOnce";
import {
  SubpageModalLazy,
  RemovePageModalLazy,
  AddNavItemModalLazy,
  CheckLinksModalLazy,
  SelectionModalLazy,
  PublishLimitModalLazy,
  PublishSubpagesLimitModalLazy,
  SubpagesLimitModalLazy,
  RemoveBrandingModalLazy,
  BlocksLimitModalLazy,
} from "../modals/lazyModals";
import { pageService } from "../../service/Page/usePageService";
import FileUploadComponent from "../FileUpload/FileUploadComponent";
interface GlobalComponentsProps {
  screenShotService: ScreenShotService;
  apolloClient: any;
}

export const GlobalComponents = ({
  screenShotService,
  apolloClient,
}: GlobalComponentsProps) => {
  const lng = useGetUrlLocale();
  const dispatch = useDispatch();
  const page = pageService.getPage();
  const { changeLanguage } = useClickHandlers(dispatch);
  const { isFormLocation } = useCheckLocation();
  const { makePageScreenshot } = useScreenShot(screenShotService, apolloClient);
  const [removeSocials] = useDeletePageSocialLinksMutation();

  const showGlobalFileManager = useSelector((state: RootState) => state.modal.globalFileManager)
  const { handleClickFileManager } = useClickHandlers(dispatch);

  const removePageModal = useSelector(
    (state: RootState) => state.modal.removePageModal,
  );
  const subPageModal = useSelector(
    (state: RootState) => state.modal.subPageModal,
  );
  const selectionModal = useSelector(
    (state: RootState) => state.modal.selectionModal,
  );
  const addMenuItemModal = useSelector(
    (state: RootState) => state.modal.addMenuItemModal,
  );
  const checkLinksModal = useSelector(
    (state: RootState) => state.modal.checkLinksModal,
  );
  const publishLimitModal = useSelector(
    (state: RootState) => state.modal.publishLimitModal,
  );
  const publishSubpagesLimitModal = useSelector(
    (state: RootState) => state.modal.publishSubpagesLimitModal,
  );
  const subpagesLimitModal = useSelector(
    (state: RootState) => state.modal.subpagesLimitModal,
  );
  const removeBrandingModal = useSelector(
    (state: RootState) => state.modal.removeBrandingModal,
  );
  const blocksLimitModal = useSelector(
    (state: RootState) => state.modal.blocksLimitModal,
  );
  const nodes = useSelector((state: RootState) => state.nodes.present.value);
  const mainNode: PageNode = useSelector(
    (state: RootState) => state.nodes.present.value[0],
  );

  const removePageSocialsNetworks = async () => {
    await removeSocials({
      variables: { pageId: page?.id },
    });
  };

  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "lang"
        ) {
          const newLang = document.documentElement.lang;

          if (newLang && newLang !== lng && LOCALES.includes(newLang)) {
            changeLanguage(newLang);
          }
        }
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["lang", "class"],
    });

    return () => observer.disconnect();
  }, [lng]);

  useEffect(() => {
    if (page?.pageSocial && page?.pageSocial.length && nodes.length) {
      let updatedNodes: PageNode[] = nodes;

      page.pageSocial.forEach(({ typeName, name, link }) => {
        updatedNodes = SocialNetworkService.initSocialsInPage(
          updatedNodes,
          name ?? '',
          link ?? '',
          typeName ?? '',
        );
      });

      dispatch(setNodes({ nodes: updatedNodes, persist: true }));
      dispatch(changeNode({ activeNode: mainNode, editedNode: updatedNodes }));
      removePageSocialsNetworks();
      pageService.setPage({ ...page, pageSocial: [] });
    }
  }, [page, nodes]);

  useEffect(() => {
    if (!isFormLocation) {
      makePageScreenshot();
    }
  }, [page]);

  return (
    <>
      <GlobalWidgets />
      <RenderOnce when={removePageModal}>
        <RemovePageModalLazy />
      </RenderOnce>
      <RenderOnce when={subPageModal}>
        <SubpageModalLazy />
      </RenderOnce>
      <RenderOnce when={addMenuItemModal}>
        <AddNavItemModalLazy />
      </RenderOnce>
      <RenderOnce when={checkLinksModal}>
        <CheckLinksModalLazy />
      </RenderOnce>
      <RenderOnce when={selectionModal}>
        <SelectionModalLazy />
      </RenderOnce>
      <RenderOnce when={publishLimitModal}>
        <PublishLimitModalLazy />
      </RenderOnce>
      <RenderOnce when={publishSubpagesLimitModal}>
        <PublishSubpagesLimitModalLazy />
      </RenderOnce>
      <RenderOnce when={subpagesLimitModal}>
        <SubpagesLimitModalLazy />
      </RenderOnce>
      <RenderOnce when={removeBrandingModal}>
        <RemoveBrandingModalLazy />
      </RenderOnce>
      <RenderOnce when={blocksLimitModal}>
        <BlocksLimitModalLazy />
      </RenderOnce>

      {showGlobalFileManager && 
        <FileUploadComponent 
            onClose={() => handleClickFileManager(false)}
            allowedFileTypes={ALL_ALLOWED_FILES}
        />
      }
    </>
  );
};
